// src/features/imprint/stores/useImprintStore.js
/**
 * @typedef {Object} ImprintSection
 * @property {string} id
 * @property {string} label
 * @property {boolean} isAlwaysOpen
 * @property {boolean} isOpen
 * @property {string|null} meta
 */

/**
 * @typedef {Object} ImprintStore
 * @property {Object|null} fullJson
 * @property {ImprintSection[]} sections
 * @property {(fullJson: Object|null) => void} setFullJson
 * @property {(id: string) => boolean} canOpenSection
 * @property {(id: string) => void} toggleSectionOpen
 * @property {(id: string) => boolean} isSectionOpen
 * @property {(id: string, meta: string|null) => void} setSectionMeta
 */

import { defineStore } from 'pinia';
import { getSections, IMPRINT_SECTIONS_STATIC } from '../static_data/sections.js';
import { useAppStore } from './useAppStore';
import { useCalendarStore } from './useCalendarStore';

export const useImprintStore = defineStore('imprint', {
    state: () => ({
        // Full product JSON payload (plain object).
        // Minimum expected shape for price matrix:
        // { configurable_array: { [methodKey]: { prices?: { tier_price: [] }, prices_2?: { tier_price: [] } } } }
        fullJson: null,

        // Sections UI state for the imprint flow.
        // Sections will be initialized in initializeSections() based on application type
        // Do NOT initialize sections here with IMPRINT_SECTIONS_STATIC to avoid race conditions
        sections: [],

        // Selected imprint method data (e.g., Screenprint, Embroidery, etc.)
        selectedImprintMethod: {
            id: null,
            sku: null,
            name: null,
            tabName: null,
            data: null
        },

        // Selected artwork method (how user provides artwork)
        selectedArtworkMethod: null,  // 'new', 'send', 'previous', 'design',

        sizeUpcharge:{},

        colorUpcharge:{},

        // Additional imprint location fees (loaded from metafield DOM element)
        // Structure: { [sku]: { additional_location_fees: [{fee_value, fee_qtymin}] } }
        additionalLocationFees: null
    }),

    getters: {
        // Convenience accessor; never throws.
        configurableArray(state) {
            return state.fullJson?.configurable_array || {};
        },

        // Rows: keys of configurable_array (as requested for now).
        imprintMethodKeys() {
            return Object.keys(this.configurableArray);
        },

        // Columns: union of QtyMin across ALL methods and both price sets (prices / prices_2).
        // Sorted asc.
        quantityBreaks() {
            const set = new Set();

            for (const key of this.imprintMethodKeys) {
                const methodObj = this.configurableArray?.[key] || {};

                const tier1 = methodObj?.prices?.tier_price || [];
                const tier2 = methodObj?.prices_2?.tier_price || [];

                for (const row of tier1) {
                    if (row?.QtyMin != null) set.add(Number(row.QtyMin));
                }
                for (const row of tier2) {
                    if (row?.QtyMin != null) set.add(Number(row.QtyMin));
                }
            }

            return Array.from(set)
                .filter((n) => !Number.isNaN(n))
                .sort((a, b) => a - b);
        },
        currentMethodQuantityBreaks(state) {
            const sku = state.selectedImprintMethod?.sku;
            if (!sku) return [];

            const methodObj = this.configurableArray?.[sku] || {};
            const set = new Set();

            const tier1 = methodObj?.prices?.tier_price || [];
            const tier2 = methodObj?.prices_2?.tier_price || [];

            for (const row of tier1) {
                if (row?.QtyMin != null) set.add(Number(row.QtyMin));
            }
            for (const row of tier2) {
                if (row?.QtyMin != null) set.add(Number(row.QtyMin));
            }

            return Array.from(set)
                .filter((n) => !Number.isNaN(n))
                .sort((a, b) => a - b);
        },

        // Get available imprint methods sorted by price
        // TODO can be improved: we have unsorted and sorted methods:
        // unsorted                imprintMethodKeys() - getter
        // sorted by price method  availableImprintMethods() - getter
        availableImprintMethods(state) {
            const methods = [];
            const configArray = state.fullJson?.configurable_array;

            if (!configArray) return [];

            for (const key in configArray) {
                const methodData = configArray[key];
                if (methodData.status?.toLowerCase() === 'disabled') {
                    continue;
                }
                methods.push({
                    sku: key,
                    name: methodData.name,
                    description: methodData.imprint_description || '',
                    image: methodData.imprint_image || null,
                    price: Number(methodData.prices_2?.min_price || 0),
                    priceSort: Number(methodData.prices_2?.min_price || 0)
                });
            }

            // Sort by price
            return methods.sort((a, b) => {
                if (a.priceSort > b.priceSort) return 1;
                if (a.priceSort < b.priceSort) return -1;
                return 0;
            });
        },

        // Get configurable_2_data for current imprint method
        currentConfigurable2Data(state) {
            if (!state.selectedImprintMethod.sku) return null;
            return state.fullJson?.configurable_array[state.selectedImprintMethod.sku]?.configurable_2_data;
        },

        // Get imprint locations for current method
        availableLocations(state) {
            const configData = state.fullJson?.configurable_array[state.selectedImprintMethod.sku]?.configurable_2_data;
            
            if (!configData?.imprint_position?._imprints) return [];
            
            return configData.imprint_position._imprints.sort((a, b) => a.sort - b.sort);
        },

        // Get available colors for current method
        availableColors(state) {
            const configData = state.fullJson?.configurable_array[state.selectedImprintMethod.sku]?.configurable_2_data;
            
            if (!configData?.imprint_options) return [];
            
            return configData.imprint_options
                .filter(opt => opt._custom_option_type === 'drop_down')
                .flatMap(opt => opt._colors || []);
        },

        // Alias for fullJson (for compatibility)
        productData(state) {
            return { fullJson: state.fullJson };
        },

        /**
         * Check if additional location fee data is available for the current imprint method
         * Button "Add Another Location" should be disabled when this returns false
         */
        hasAdditionalLocationFeeData(state) {
            const sku = state.selectedImprintMethod?.sku;
            if (!sku || !state.additionalLocationFees) return false;
            return !!state.additionalLocationFees?.[sku];
        },

        /**
         * Get additional location fee tiers for the current imprint method
         * Returns sorted array of { fee_value, fee_qtymin } descending by fee_qtymin
         */
        currentAdditionalLocationFeeTiers(state) {
            const sku = state.selectedImprintMethod?.sku;
            if (!sku || !state.additionalLocationFees) return [];
            const tiers = state.additionalLocationFees?.[sku]?.additional_location_fees || [];
            return [...tiers].sort((a, b) => b.fee_qtymin - a.fee_qtymin);
        },

        // Get imprint method key
        getImprintMethodKey(state){
             return state.selectedImprintMethod.name;
        },

        // Get qty_increment for current imprint method
        currentQtyIncrement(state) {
            const currentSku = state.selectedImprintMethod?.sku;
            if (!currentSku) return 1;
            
            const qtyIncrement = state.fullJson?.configurable_array?.[currentSku]?.qty_increment;
            return Number(qtyIncrement) || 1;
        }
    },

    actions: {
        setFullJson(fullJson) {
            this.fullJson = fullJson || null;
            console.log('[ImprintStore] setFullJson called');
            console.log('[ImprintStore] fullJson:', fullJson ? 'loaded' : 'null');
            console.log('[ImprintStore] configurable_array:', fullJson?.configurable_array ? Object.keys(fullJson.configurable_array) : 'none');
            // For calendar mode, validate bundle compatibility first
            const appStore = useAppStore();

            if (appStore.applicationType === 'calendar') {
                console.log('[DEBUG ImprintStore] Calendar mode detected, validating bundle...');
                try {
                    const calendarStore = useCalendarStore();
                    
                    console.log('[DEBUG ImprintStore] CalendarStore products:', calendarStore.products);
                    
                    const validation = calendarStore.validateBundleCompatibility();
                    
                    console.log('[DEBUG ImprintStore] Bundle validation result:', validation);
                    console.log('[DEBUG ImprintStore] Available methods after validation:', calendarStore.availablePrintMethods);
                } catch (e) {
                    console.error('[DEBUG ImprintStore] Error validating bundle:', e);
                }
            }
            
            this.initImprintMethod();
        },

        // Initialize selectedImprintMethod with first available method sorted by price
        initImprintMethod(){
            console.log('[ImprintStore] initImprintMethod called');
            console.log('[ImprintStore] fullJson exists:', !!this.fullJson);
            console.log('[ImprintStore] configurable_array exists:', !!this.fullJson?.configurable_array);
            
            if ( this.fullJson?.configurable_array) {
                // Use the getter to get sorted methods by price
                const sortedMethods = this.availableImprintMethods;
                console.log('[ImprintStore] Sorted methods:', sortedMethods.map(m => ({ sku: m.sku, name: m.name, price: m.price })));

                if (sortedMethods.length > 0) {
                    let firstMethod = sortedMethods[0];
                    
                    const appStore = useAppStore();
                    if (appStore.applicationType === 'calendar') {
                        console.log('[DEBUG ImprintStore] Calendar mode - checking available methods');
                        try {
                            const calendarStore = useCalendarStore();
                            const availableForBundle = calendarStore.availablePrintMethods;
                            
                            console.log('[DEBUG ImprintStore] availableForBundle:', availableForBundle);
                            
                            if (availableForBundle && availableForBundle.length > 0) {
                                const availableMethod = sortedMethods.find(m => availableForBundle.includes(m.sku));
                                console.log('[DEBUG ImprintStore] Found available method:', availableMethod);
                                
                                if (availableMethod) {
                                    firstMethod = availableMethod;
                                    console.log('[DEBUG ImprintStore] Selected first available method:', firstMethod.sku);
                                } else {
                                    console.warn('[DEBUG ImprintStore] No available methods found in sorted list');
                                    return;
                                }
                            } else if (availableForBundle && availableForBundle.length === 0 && calendarStore.products.length > 0) {
                                console.warn('[DEBUG ImprintStore] No methods available for bundle');
                                return;
                            }
                        } catch (e) {
                            console.error('[DEBUG ImprintStore] Error checking bundle availability:', e);
                        }
                    }
                    
                    const methodData = this.fullJson.configurable_array[firstMethod.sku];

                    this.setSelectedImprintMethod({
                        id: sortedMethods.indexOf(firstMethod),
                        sku: firstMethod.sku,
                        name: firstMethod.name,
                        data: methodData
                    });
                    console.log('[ImprintStore] Auto-initialized imprint method (sorted by price):', firstMethod.sku, 'Price:', firstMethod.price);
                } else {
                    console.warn('[ImprintStore] No methods available to initialize');
                }
            } else {
                console.warn('[ImprintStore] Cannot initialize imprint method: fullJson.configurable_array not available');
            }
        },

        initUpcharges(sku) {
            console.log('[ImprintStore] initUpcharges called for sku:', sku);
            
            let upcharge = null;
            let colorUpcharge = null;
            
            // Check if method data exists
            const methodData = this.fullJson?.configurable_array?.[sku];
            if (!methodData) {
                console.warn('[ImprintStore] Method data not found for sku:', sku);
                return;
            }
            
            // Check if attribute exists (may not exist for calendar products)
            const attribute = methodData.attribute;
            if (!attribute || !Array.isArray(attribute)) {
                console.warn('[ImprintStore] No attribute array found for sku:', sku, '(this is normal for calendar products)');
                // Set empty upcharges for calendar products
                this.sizeUpcharge = {};
                this.colorUpcharge = {};
                return;
            }
            
            console.log('[ImprintStore] Attribute array:', attribute);

            if (attribute.length == 1) {
                colorUpcharge = attribute[0].code == 'color' ? attribute[0].upcharge : null;
            } else if (attribute.length == 2) {
                upcharge = attribute[1].code == 'size' ? attribute[1].upcharge : null;
                colorUpcharge = attribute[0].code == 'color' ? attribute[0].upcharge : null;
            }
            
            console.log('[ImprintStore] Size Upcharge:', upcharge);
            console.log('[ImprintStore] Color Upcharge:', colorUpcharge);
            // }
            // Set proof_fee
            /*For Calendar Envelop product fee data is missing in full json (imprint_data) */
            /* if(Object.hasOwn(this.imprintData,'imprint_data')
                 && Object.hasOwn(this.imprintData.imprint_data , 'setup_fee')
                 && Object.hasOwn(this.imprintData.imprint_data , 'proof_fee'))
             {
                 this.store.perItemFee.proof_fee = imprintData.imprint_data.proof_fee;
                 this.store.perItemFee.setupFee = imprintData.imprint_data.setup_fee;
             }*/
            // TODO improve duplicated code
            //debugger;


            if (!upcharge) {
                console.error(" >>>>  1 attribute upcharge not found /ImprintTabs/setTierData <<<<");
            } else {
                let result = {};
                upcharge.forEach(
                    item => {
                        // if(item.store == this.store.storeID) {
                        result[item.option] = item.price;
                        // }
                    }
                );

                this.sizeUpcharge = result;
            }

            if (!colorUpcharge) {
                console.error(" >>>> 2 attribute upcharge not found /ImprintTabs/setTierData <<<<");
            } else {
                let result = {};
                colorUpcharge.forEach(
                    item => {
                        // if(item.store == this.store.storeID) {
                        let label = item.option.toLowerCase();
                        result[label] = {
                            price: item.price,
                            price_type: item.price_type
                        }
                        // }
                    }
                );

                this.colorUpcharge = result;
            }

            // }
        },

        // Set selected imprint method (Screenprint, Embroidery, etc.)
        setSelectedImprintMethod({ id, sku, name, data }) {
            if (id !== undefined) this.selectedImprintMethod.id = id;
            if (sku) this.selectedImprintMethod.sku = sku;
            if (name) {
                this.selectedImprintMethod.name = name;
                this.selectedImprintMethod.tabName = name;
            }
            if (data) this.selectedImprintMethod.data = data;
            
            console.log('[ImprintStore] Selected imprint method set:', {
                id: this.selectedImprintMethod.id,
                sku: this.selectedImprintMethod.sku,
                name: this.selectedImprintMethod.name
            });

            this.initUpcharges(sku);
        },

        // Set selected artwork method (how user provides artwork)
        setSelectedArtworkMethod(methodId) {
            this.selectedArtworkMethod = methodId;
            console.log('[ImprintStore] Selected artwork method:', methodId);
        },

        canOpenSection(id) {
            return true;
        },

        toggleSectionOpen(id) {
            if (!this.canOpenSection(id)) {
                return;
            }

            this.sections = this.sections.map((section) => {
                if (section.id === id && section.isAlwaysOpen) {
                    return { ...section, isOpen: true };
                }

                if (section.id === id) {
                    return { ...section, isOpen: !section.isOpen };
                }

                return section;
            });
        },

        isSectionOpen(id) {
            const section = this.sections.find((s) => s.id === id);
            return !!(section && section.isOpen);
        },

        setSectionMeta(id, meta) {
            this.sections = this.sections.map((section) => {
                if (section.id === id) {
                    return { ...section, meta };
                }
                return section;
            });
        },

        /**
         * Initialize additional location fees from Liquid metafield DOM element
         * Called once on app mount
         */
        initAdditionalLocationFees() {
            try {
                const el = document.getElementById('additional_location_fee');
                if (!el) {
                    console.warn('[ImprintStore] #additional_location_fee element not found');
                    return;
                }
                const raw = (el.textContent || '').trim();
                if (!raw) {
                    console.warn('[ImprintStore] #additional_location_fee is empty');
                    return;
                }
                const parsed = JSON.parse(raw);
                // The metafield may wrap data in configurable_array or be the map directly
                this.additionalLocationFees = parsed?.configurable_array || parsed;
                console.log('[ImprintStore] Additional location fees loaded:', Object.keys(this.additionalLocationFees));
            } catch (e) {
                // console.error('[ImprintStore] Failed to parse additional_location_fee:', e);
            }
        },

        /**
         * Calculate additional location fee per piece for a given quantity
         * Uses the same tier logic as product pricing (highest qualifying tier)
         * @param {number} totalQty
         * @returns {number} fee per piece
         */
        getAdditionalLocationFeePerPiece(totalQty) {
            const tiers = this.currentAdditionalLocationFeeTiers;
            if (!tiers || tiers.length === 0) return 0;
            for (const tier of tiers) {
                if (totalQty >= Number(tier.fee_qtymin)) {
                    return Number(tier.fee_value);
                }
            }
            // Fallback: lowest tier (last after desc sort = smallest qtymin)
            return Number(tiers[tiers.length - 1]?.fee_value || 0);
        },

        unlockSection(id) {
            // Method to unlock/enable a section (placeholder for now)
            // In future, this could handle section validation/requirements
            console.log('[ImprintStore] Unlocking section:', id);
            // Currently all sections are always unlockable (canOpenSection returns true)
            // This method is here for future functionality
        },
        
        /**
         * Initialize sections based on application type
         * This should be called after appStore is initialized
         * @param {string} applicationType - 'default' | 'calendar' | 'calendar_single'
         */
        initializeSections(applicationType = 'default') {
            console.log('[ImprintStore] Initializing sections for application type:', applicationType);
            
            const sectionsConfig = getSections(applicationType);
            
            this.sections = sectionsConfig.map((s) => {
                const section = {
                    ...s,
                    meta: s.meta || null,
                    isOpen: !!s.isAlwaysOpen || !!s.isInitiallyOpen
                };
                console.log('[ImprintStore] Initialized section:', section.id, 'isOpen:', section.isOpen, 'isInitiallyOpen:', s.isInitiallyOpen);
                return section;
            });
            
            console.log('[ImprintStore] Sections initialized: ', this.sections.map(s => ({ id: s.id, label: s.label, isOpen: s.isOpen })));
        }
    }
});
